// Removed duplicate getStatusIcon function from top-level scope.
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
// Removed EmeraldIconModule import
import { Subscription } from 'rxjs';
import {
  StatusApiService,
  StatusCategory,
  StatusOption,
} from '../../services/status-api.service';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
export type StatusType =
  | 'Pending'
  | 'Done'
  | 'Initiated'
  | 'Cancelled'
  | 'Ready'
  | 'Delivered'
  | 'Cost Received'
  | 'Exception Received';

interface StatusConfig {
  label: string;
  colorClass: string;
  description?: string;
}

const RETURN: Record<string, string> = {
  Pending: 'HOME',
  Done: 'check_circle',
  Initiated: 'autorenew',
  Cancelled: 'cancel',
};

const REQUEST: Record<string, string> = {
  Initiated: 'check',
  Pending: 'pendingrounded',
  Ready: 'working',
  Delivered: 'thumb_up',
  Cancelled: 'cancel',
};

const INVENTORY: Record<string, string> = {
  Pending: 'pendingrounded',
  Delivered: 'check_circle',
};

const DAMAGE: Record<string, string> = {
  Pending: 'pendingrounded',
  'Cost Received': 'payment',
  'Exception Received': 'warning',
};

const ALL_ICON_MAP: Record<string, string> = {
  ...RETURN,
  ...REQUEST,
  ...INVENTORY,
  ...DAMAGE,
};

export const STATUS_MAP: Record<string, StatusConfig> = {
  Pending: {
    label: 'Pending',
    colorClass: 'status-pending',
    description: 'Awaiting processing',
  },
  Done: {
    label: 'Done',
    colorClass: 'status-done',
    description: 'Completed successfully',
  },
  Initiated: {
    label: 'Initiated',
    colorClass: 'status-initiated',
    description: 'Process started',
  },
  Cancelled: {
    label: 'Cancelled',
    colorClass: 'status-cancelled',
    description: 'Process cancelled',
  },
  Ready: {
    label: 'Ready',
    colorClass: 'status-ready',
    description: 'Ready for next step',
  },
  Delivered: {
    label: 'Delivered',
    colorClass: 'status-delivered',
    description: 'Successfully delivered',
  },
  'Cost Received': {
    label: 'Cost Received',
    colorClass: 'status-cost-received',
    description: 'Cost information received',
  },
  'Exception Received': {
    label: 'Exception Received',
    colorClass: 'status-exception-received',
    description: 'Exception flagged and received',
  },
};

@Component({
  selector: 'app-status-label',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    MatIcon,
  ],
  templateUrl: './status-label.component.html',
  styleUrls: ['./status-label.component.scss'],
})
export class StatusLabelComponent implements OnInit, OnDestroy {
  @ViewChild('matSelect', { static: true }) matSelect!: MatSelect;
  // Map status to icon color
  public statusIconColors: Record<string, string> = {
    HOME: '#6366f1',
    check_circle: '#489969',
    check: '#28573C',
    autorenew: '#3b82f6',
    cancel: '#ef4444',
    pendingrounded: '#D5710C',
    CardGiftcardOutlinedIcon: '#8b5cf6',
    thumb_up: '#06b6d4',
    local_shipping: '#059669',
    payment: '#489969',
    warning: '#A03530',
    help_outline: '#666',
    working: '#10b981',
  };

  getStatusIconColor(option: any): string {
    return this.statusIconColors[this.getStatusIcon(option)] || '#666';
  }
  getSelectedIcon(arg0: any) {
    throw new Error('Method not implemented.');
  }
  selectedOption: StatusOption | null = null;
  /**
   * Returns the icon name for a given status option.
   */
  public getStatusIcon(option: any): string {
    if (typeof option === 'string') {
      return ALL_ICON_MAP[option] || 'help_outline';
    }
    if (option && typeof option.icon === 'string') {
      return option.icon || 'help_outline';
    }
    return 'help_outline';
  }
  private _statusV: any = '';
  @Input()
  set statusV(val: StatusType | null | undefined) {
    this._statusV = this.getDefaultedStatus(val);
    this.syncSelectedOption();
  }
  get statusV(): StatusType {
    return this._statusV;
  }

  private getDefaultedStatus(val: StatusType | null | undefined): StatusType {
    if (
      val == null ||
      String(val) === '' ||
      (typeof val === 'string' && val.trim() === '')
    ) {
      if (this.category === 'Request' || this.category === 'Return')
        return 'Initiated';
      if (this.category === 'Damage' || this.category === 'Inventory')
        return 'Pending';
    }
    return val as StatusType;
  }

  @Input() showDropdown: boolean = false;
  @Input() readonly: boolean = false;
  @Input() storageKey: string = 'app_status_selected';
  @Input() category: any = '';
  @Output() statusChange = new EventEmitter<StatusType>();

  dropdownId: string = 'status_select';
  selectOptions: StatusOption[] = [];
  open = false;
  statusOptions: StatusType[] = [];

  private subscription = new Subscription();

  constructor(private statusApiService: StatusApiService) {
    // Initialize with empty array to prevent undefined errors
    this.selectOptions = [];
    this.statusOptions = [];
  }

  get config(): StatusConfig | undefined {
    const key = Object.keys(STATUS_MAP).find(
      (k) => k.toLowerCase() === (this.statusV || '').toLowerCase(),
    );
    return key ? STATUS_MAP[key] : STATUS_MAP['Pending'];
  }

  onStatusChange(value: StatusType) {
    this.statusChange.emit(value);
  }

  onDropdownChange(option: StatusOption) {
    if (option && this.statusOptions.includes(option.value as StatusType)) {
      this.selectedOption = option;
      this.statusV = option.value as StatusType;
      this.statusChange.emit(this.statusV);
    }
  }

  toggleOpen() {
    this.open = !this.open;
  }
  close() {
    this.open = false;
  }

  selectFromString(value: string) {
    if (this.isValidStatusType(value)) {
      this.select(value as StatusType);
    }
  }

  select(val: StatusType) {
    if (this.statusV !== val) {
      this.statusV = val;
    }
    this.close();
  }

  private isValidStatusType(value: string): value is StatusType {
    return this.statusOptions
      .map((s) => s.toLowerCase())
      .includes((value || '').toLowerCase());
  }

  iconFor(val: StatusType): string {
    return ALL_ICON_MAP[val] || 'help_outline';
  }

  ngOnInit(): void {
    // Load fallback options first to prevent undefined errors
    this.loadFallbackStatuses();
    // Then try to load from API
    this.loadStatusesForCategory();
    this._statusV = this.getDefaultedStatus(this._statusV);
    this.syncSelectedOption();
  }
  ngOnChanges(): void {
    this._statusV = this.getDefaultedStatus(this._statusV);
    this.syncSelectedOption();
  }

  private syncSelectedOption(): void {
    if (this.selectOptions && this.selectOptions.length > 0) {
      const found = this.selectOptions.find(
        (opt) =>
          (opt.value || '').toLowerCase() ===
          (this.statusV || '').toLowerCase(),
      );
      this.selectedOption = found || null;
    } else {
      this.selectedOption = null;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadStatusesForCategory(): void {
    const apiSub = this.statusApiService
      .getStatusesForCategory(this.category)
      .subscribe({
        next: (statuses: string[]) => {
          this.statusOptions = statuses as StatusType[];
          this.selectOptions = statuses.map((status) => {
            const icon = this.iconFor(status as StatusType);
            const color = this.statusIconColors[icon] || '#666';
            return {
              value: status,
              label: status,
              icon,
              color,
              colorClass: STATUS_MAP[status]?.colorClass || 'status-default',
              category: this.category,
            };
          });
          this.syncSelectedOption();
        },
        error: (error) => {
          console.warn(
            'Failed to load statuses for category:',
            this.category,
            error,
          );
          this.loadFallbackStatuses();
        },
      });
    this.subscription.add(apiSub);
  }

  private loadFallbackStatuses(): void {
    const fallbackMap: Record<StatusCategory, string[]> = {
      Return: ['Pending', 'Done', 'Initiated', 'Cancelled'],
      Request: ['Initiated', 'Pending', 'Ready', 'Delivered', 'Cancelled'],
      Damage: ['Pending', 'Cost Received', 'Exception Received'],
      Inventory: ['Pending', 'Delivered'],
    };

    const categoryKey = this.category as StatusCategory;
    const statuses = fallbackMap[categoryKey] || ['Pending'];
    this.statusOptions = statuses as StatusType[];
    this.selectOptions = statuses.map((status) => {
      const icon = this.iconFor(status as StatusType);
      const color = this.statusIconColors[icon] || '#666';
      return {
        value: status,
        label: status,
        icon,
        color,
        colorClass: STATUS_MAP[status]?.colorClass || 'status-default',
        category: this.category,
      };
    });
    this.syncSelectedOption();
  }
}
